import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';

export default function About() {
  const { t } = useTranslation('common');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: 'ri-home-heart-line',
      title: t('about.feature1Title'),
      description: t('about.feature1Desc')
    },
    {
      icon: 'ri-award-line',
      title: t('about.feature2Title'),
      description: t('about.feature2Desc')
    },
    {
      icon: 'ri-price-tag-3-line',
      title: t('about.feature3Title'),
      description: t('about.feature3Desc')
    }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-amber-50/30">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <p className="text-amber-700 text-xs font-semibold tracking-[0.2em] mb-4 uppercase">{t('about.badge')}</p>
          <h2 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">
            {t('about.title')}
          </h2>
          <div className="w-12 h-px bg-amber-400 mx-auto mb-6"></div>
          <p className="text-stone-600 text-base max-w-3xl mx-auto leading-relaxed font-light">
            {t('about.subtitle')}
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Image */}
          <div className={`relative transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative rounded-2xl overflow-hidden" style={{ width: '100%', height: '580px' }}>
              <img 
                src="https://public.readdy.ai/ai/img_res/8523cea40b4afab11727899022ac1154.jpg"
                alt="JS Glass Showroom"
                className="w-full h-full object-cover object-center"
              />
            </div>
            {/* Floating accent card */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-6 max-w-[200px]">
              <div className="text-4xl font-light text-stone-800 mb-1">25<span className="text-amber-500">+</span></div>
              <div className="text-stone-500 text-xs tracking-wider">{t('about.stat1')}</div>
            </div>
          </div>

          {/* Right - Content */}
          <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <p className="text-stone-600 text-sm leading-[1.8] mb-10">
              {t('about.description')}
            </p>

            {/* Features */}
            <div className="space-y-6 mb-10">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-5 group">
                  <div className="w-11 h-11 flex items-center justify-center bg-amber-50 rounded-xl flex-shrink-0 group-hover:bg-amber-500 transition-colors duration-300">
                    <i className={`${feature.icon} text-amber-600 text-xl group-hover:text-white transition-colors duration-300`}></i>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-stone-800 mb-1">{feature.title}</h3>
                    <p className="text-stone-500 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Stats Row */}
            <div className="flex items-center gap-10 pt-8 border-t border-stone-200">
              <div>
                <div className="text-3xl font-light text-stone-800">1000<span className="text-amber-500">+</span></div>
                <div className="text-stone-400 text-xs tracking-wider mt-1">{t('about.stat2')}</div>
              </div>
              <div className="w-px h-12 bg-stone-200"></div>
              <div>
                <div className="text-3xl font-light text-stone-800">98<span className="text-amber-500">%</span></div>
                <div className="text-stone-400 text-xs tracking-wider mt-1">{t('about.stat3')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}